import { useState, useMemo, useEffect } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import {
  Visibility,
  Edit,
  Delete,
  Add,
  SettingsRounded,
  WarningAmber,
  AppsRounded,
  ContentCopy
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Switch,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Avatar,
  Stack,
  CircularProgress
} from "@mui/material";
import ClientsUpdate from "./clients-update";
import ClientsDetail from "./clients-detail";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import moment from "moment";
import Error from "../error";
import useUserPermissions from "../../hooks/useUserPermissions";
import {
  mrtManagementTableCustomActionsOuterSx,
  mrtManagementTablePrimaryActionButtonSx,
  mrtManagementTableToolbarOptions,
} from "../../utils/mrtManagementTableToolbar";

// Clients Management component powered by Material React Table
function Clients() {
  const {
    createClient,
    getAllClients,
    getClient,
    deleteClient,
    updateClient,
    updateClientStatus,
  } = useUserPermissions();

  // MRT State Management
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });

  // Dialog & View State
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(false);
  const [update, setUpdate] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientData, setSelectedClientData] = useState({});

  // Safety Confirmation States
  const [statusConfirm, setStatusConfirm] = useState({ open: false, client: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, client: null });
  const [editConfirm, setEditConfirm] = useState({ open: false, client: null });

  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  // Data Fetching Logic (Server-Side)
  const {
    isLoading: clientsDataLoading,
    data: clientsData,
    refetch,
    error: fetchClientsError,
  } = useQuery(
    ["clients", pagination, globalFilter, columnFilters, sorting],
    () => {
      const filterParams = globalFilter
        ? JSON.stringify([{
          column_field: "*",
          operator_value: "contains",
          value: JSON.stringify({
            columns: ["name", "client_type", "scopes"],
            value: globalFilter
          })
        }])
        : columnFilters.length > 0
          ? JSON.stringify(columnFilters.map(f => ({
            column_field: f.id,
            operator_value: "contains",
            value: f.value
          })))
          : "[]";

      const sortParams = JSON.stringify(sorting.map(s => ({
        field: s.id,
        sort: s.desc ? "desc" : "asc"
      })));

      return axiosPrivate.get(`/clients`, {
        params: {
          page: pagination.pageIndex,
          per_page: pagination.pageSize,
          sort: sortParams,
          filter: filterParams,
        },
      });
    },
    { refetchOnWindowFocus: false, enabled: !!getAllClients }
  );

  // Mutations
  const { mutate: toggleStatus, isLoading: isToggling } = useMutation((client) =>
    axiosPrivate.patch(`/clients/${client.id}/status`, { status: client.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" }),
    {
      onSuccess: () => {
        enqueueSnackbar("Status updated successfully", { variant: "success" });
        setStatusConfirm({ open: false, client: null });
        refetch();
      }
    }
  );

  const { mutate: deleteClientMutate, isLoading: isDeleting } = useMutation((id) =>
    axiosPrivate.delete(`/clients/${id}`),
    {
      onSuccess: () => {
        enqueueSnackbar("Client deleted successfully", { variant: "success" });
        setDeleteConfirm({ open: false, client: null });
        refetch();
      }
    }
  );

  // Handlers
  const handleView = (id) => {
    setSelectedClientId(id);
    setView(true);
    setOpen(true);
  };

  const handleEditRequest = (client) => {
    setSelectedClientData(client);
    setEditConfirm({ open: true, client });
  };

  const handleProceedEdit = () => {
    setSelectedClientId(selectedClientData.id);
    setUpdate(true);
    setView(false);
    setOpen(true);
    setEditConfirm({ open: false, client: null });
  };

  const handleOpenCreate = () => {
    setUpdate(false);
    setView(false);
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
    setUpdate(false);
    setView(false);
  };

  // Columns Definition
  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Client Application",
      size: 250,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.lighter', color: 'primary.main', borderRadius: 2 }}>
            <AppsRounded fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main' }}>
              {row.original.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '11px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              ID: {row.original.id}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      accessorKey: "client_type",
      header: "Type",
      size: 150,
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 700,
            textTransform: 'capitalize',
            borderColor: 'divider',
            fontSize: '11px'
          }}
        />
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 150,
      Cell: ({ cell, row }) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={cell.getValue()}
            size="small"
            sx={{
              fontWeight: 800,
              fontSize: '11px',
              bgcolor: cell.getValue() === "ACTIVE" ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
              color: cell.getValue() === "ACTIVE" ? '#2e7d32' : '#d32f2f',
            }}
          />
          <Switch
            checked={cell.getValue() === "ACTIVE"}
            onChange={() => setStatusConfirm({ open: true, client: row.original })}
            size="small"
            disabled={!updateClientStatus}
          />
        </Stack>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created",
      size: 150,
      Cell: ({ cell }) => moment(cell.getValue()).format("MMM D, YYYY"),
    },
  ], [updateClientStatus]);

  const table = useMaterialReactTable({
    columns,
    data: clientsData?.data?.data || [],
    initialState: { showColumnFilters: false, density: 'comfortable' },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    ...mrtManagementTableToolbarOptions,
    muiToolbarAlertBannerProps: fetchClientsError ? { color: 'error', children: 'Error loading data' } : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: clientsData?.data?.meta_data?.total || 0,
    state: { columnFilters, globalFilter, isLoading: clientsDataLoading, pagination, sorting },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Details">
          <IconButton size="small" onClick={() => handleView(row.original.id)} color="inherit" sx={{ opacity: 0.7 }}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
        {updateClient && (
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditRequest(row.original)} color="primary">
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {deleteClient && (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => setDeleteConfirm({ open: true, client: row.original })} color="error">
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <Box sx={mrtManagementTableCustomActionsOuterSx}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'secondary.lighter', display: 'flex', flexShrink: 0 }}>
            <SettingsRounded sx={{ color: 'secondary.main' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'secondary.main', lineHeight: 1.2 }}>
              Client Applications
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {clientsData?.data?.meta_data?.total || 0} registered integrations
            </Typography>
          </Box>
        </Stack>
        {createClient && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={mrtManagementTablePrimaryActionButtonSx}
          >
            Create Client
          </Button>
        )}
      </Box>
    ),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 5,
        border: "1px solid", borderColor: "divider",
        boxShadow: '0 10px 40px rgba(0,0,0,0.02)',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        bgcolor: 'background.default',
        fontWeight: 800,
        color: 'text.secondary',
        fontSize: '12px',
        py: 2,
      },
    },
  });

  return fetchClientsError ? (
    <Error error={fetchClientsError?.response?.data?.error} />
  ) : (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100%' }}>
      <MaterialReactTable table={table} />

      {/* Modals for Detail / Update */}
      <Modal open={open} onClose={handleModalClose}>
        <Box>
          {view ? (
            <ClientsDetail handleViewModalClose={handleModalClose} clientId={selectedClientId} />
          ) : (
            <ClientsUpdate handleModalClose={handleModalClose} isNew={!update} clientData={selectedClientData} refetch={refetch} />
          )}
        </Box>
      </Modal>

      {/* Status Confirmation */}
      <SafetyDialog
        open={statusConfirm.open}
        title={statusConfirm.client?.status === 'ACTIVE' ? "Deactivate Client?" : "Activate Client?"}
        content={`Are you sure you want to change the status of ${statusConfirm.client?.name}?`}
        onConfirm={() => toggleStatus(statusConfirm.client)}
        onCancel={() => setStatusConfirm({ open: false, client: null })}
        isLoading={isToggling}
        color={statusConfirm.client?.status === 'ACTIVE' ? "error" : "success"}
      />

      {/* Delete Confirmation */}
      <SafetyDialog
        open={deleteConfirm.open}
        title="Delete Client?"
        content={`This action is permanent. Are you sure you want to delete ${deleteConfirm.client?.name}?`}
        onConfirm={() => deleteClientMutate(deleteConfirm.client?.id)}
        onCancel={() => setDeleteConfirm({ open: false, client: null })}
        isLoading={isDeleting}
        color="error"
      />

      {/* Edit Confirmation */}
      <SafetyDialog
        open={editConfirm.open}
        title="Edit Configuration?"
        content={`Adjusting ${editConfirm.client?.name} configuration may impact existing integrations. Proceed?`}
        onConfirm={handleProceedEdit}
        onCancel={() => setEditConfirm({ open: false, client: null })}
        color="warning"
      />
    </Box>
  );
}

function SafetyDialog({ open, title, content, onConfirm, onCancel, isLoading, color = "primary" }) {
  return (
    <Dialog
      open={open}
      onClose={() => !isLoading && onCancel()}
      PaperProps={{ sx: { borderRadius: 6, p: 1, maxWidth: '400px' } }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Box sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: `${color}.lighter`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2
        }}>
          <WarningAmber color={color} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>{content}</Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
        <Button fullWidth variant="outlined" color="inherit" onClick={onCancel} disabled={isLoading} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button fullWidth variant="contained" color={color} onClick={onConfirm} disabled={isLoading} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Clients;
